// 米問屋（里の見出しから開く札）。課金「米問屋の通い帳」で、兵糧を掃討チケットに替える（core の exchangeKomedoiya）。
// 手形が無ければ交換の比率とショップへの導線だけ
import { hasKomedoiya, META_CONFIG, PRODUCTS } from '@hyakki/core';
import { sfx } from '../audio';
import { api } from '../api';
import { go } from '../router';
import { act, state } from '../state';
import { fmt, resIcon } from './dom';
import { icon } from './icons';
import { openModal, toast } from './overlay';
import { errorText } from './status';

const { hyoro: H, tickets: T } = META_CONFIG.komedoiya;
const CAP = META_CONFIG.sweep.ticketCap;

/** 今の兵糧（本体＋予備）と所持の上限から、何回替えられるか */
function maxTimes() {
  const st = state.stamina;
  const hy = st ? st.value + st.reserve : (state.resources.hyoro ?? 0);
  return Math.max(0, Math.min(Math.floor(hy / H), Math.floor((CAP - (state.resources.soutou ?? 0)) / T)));
}

function html(n: number): string {
  const st = state.stamina;
  const hy = st ? st.value + st.reserve : (state.resources.hyoro ?? 0);
  const have = state.resources.soutou ?? 0;
  const owned = !!state.profile && hasKomedoiya(state.profile);
  const max = maxTimes();
  const rate = `<div class="kd-rate"><span>${resIcon('hyoro')}<b>${H}</b></span>${icon('right')}<span>${resIcon('soutou')}<b>${T}</b></span></div>`;
  const lead = `<p class="kd-lead">兵糧を掃討チケットに交換できます</p>`;
  const wallet = `${lead}<div class="kd-wallet"><span>${resIcon('hyoro')}<b>${fmt(hy)}</b></span><span>${resIcon('soutou')}<b>${fmt(have)}</b><small>/${fmt(CAP)}</small></span></div>`;
  if (!owned)
    return `${wallet}${rate}<button class="btn primary big kd-go" data-go>${icon('lock')}米問屋の通い帳${icon('right', 'chev')}</button>`;
  const k = clampN(n);
  const off = max < 1 ? 'disabled' : '';
  return `${wallet}${rate}
    <div class="kd-bar">
      <button class="btn kd-n" data-d="-1" aria-label="1つ減らす" ${off}>${icon('minus')}</button>
      <input type="range" class="kd-range" min="1" max="${Math.max(1, max)}" value="${k}" aria-label="回数" ${off}>
      <button class="btn kd-n" data-d="1" aria-label="1つ増やす" ${off}>${icon('plus')}</button>
      <button class="btn kd-n kd-max" data-max ${off}>最大</button>
    </div>
    <p class="kd-sum"><b class="kd-k"></b>${resIcon('hyoro')}<b class="kd-hy"></b>${icon('right')}${resIcon('soutou')}<b class="kd-so"></b></p>
    <button class="btn primary big kd-do" data-do ${off}>${icon('swap')}交換</button>`;
}

const clampN = (n: number) => Math.max(1, Math.min(n, Math.max(1, maxTimes())));

export function openKomedoiya() {
  let n = 1;
  const m = openModal(`<div class="kd">${html(n)}</div>`, { title: '米問屋', className: 'sheet-wrap navy-sheet kd-wrap' });
  const body = m.body.querySelector<HTMLElement>('.kd')!;
  // スライダーを動かしているあいだは作り直さず、数字とボタンだけ替える
  const setN = (v: number) => {
    n = clampN(v);
    const max = maxTimes();
    const q = <T extends Element>(s: string) => body.querySelector<T>(s);
    const range = q<HTMLInputElement>('.kd-range');
    if (!range) return;
    range.value = String(n);
    range.style.setProperty('--kd-v', String(max > 1 ? (n - 1) / (max - 1) : max));
    q('.kd-k')!.textContent = `×${fmt(n)}`;
    q('.kd-hy')!.textContent = `-${fmt(H * n)}`;
    q('.kd-so')!.textContent = `+${fmt(T * n)}`;
    if (max < 1) return;
    q<HTMLButtonElement>('[data-d="-1"]')!.disabled = n <= 1;
    q<HTMLButtonElement>('[data-d="1"]')!.disabled = n >= max;
    q<HTMLButtonElement>('[data-max]')!.disabled = n >= max;
  };
  const redraw = () => {
    body.innerHTML = html(n);
    setN(n);
  };
  setN(n);
  body.addEventListener('input', (e) => {
    const r = (e.target as HTMLElement).closest<HTMLInputElement>('.kd-range');
    if (r) setN(Number(r.value));
  });
  body.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-go]')) {
      m.close();
      return go('shop');
    }
    const d = t.closest<HTMLButtonElement>('[data-d]');
    if (d && !d.disabled) return setN(n + Number(d.dataset.d));
    if (t.closest('[data-max]')) return setN(maxTimes());
    const b = t.closest<HTMLButtonElement>('[data-do]');
    if (!b || b.disabled) return;
    b.disabled = true;
    try {
      const r = await act(api.exchangeKomedoiya(n));
      sfx('coin');
      toast(`掃討チケット +${fmt(r.tickets)}`, 'good');
      n = 1;
    } catch (err) {
      toast(errorText(err), 'error');
    }
    redraw();
  });
}

const OUT_HINT_KEY = 'hk.ticketsOutHint';
/** 掃討チケットを使い切ったとき、米問屋の通い帳を持っていない人に購入の案内（ショップの通い帳へ）。「次回以降表示しない」まで毎回 */
export function ticketsOutHint(): Promise<void> {
  if ((state.resources.soutou ?? 0) > 0 || !state.profile || state.me?.user.guest || hasKomedoiya(state.profile)) return Promise.resolve();
  try {
    if (localStorage.getItem(OUT_HINT_KEY)) return Promise.resolve();
  } catch {
    /* 端末に残せなくても出すだけ */
  }
  const prod = PRODUCTS.find((x) => x.id === 'komedoiya');
  if (!prod) return Promise.resolve();
  return new Promise((done) => {
    const m = openModal(
      `<div class="kd kd-hint">
        <p class="kd-lead">米問屋の通い帳を買うと、兵糧を掃討チケットに交換できます</p>
        <div class="kd-rate"><span>${resIcon('hyoro')}<b>${H}</b></span>${icon('right')}<span>${resIcon('soutou')}<b>${T}</b></span></div>
        <p class="kd-price">買い切り <b>¥${fmt(prod.yen)}</b></p>
        <label class="kd-hide"><input type="checkbox" data-hide>次回以降表示しない</label>
        <div class="btn-row"><button class="btn" data-close>閉じる</button><button class="btn primary" data-shop>${icon('shop')}通い帳を見る</button></div>
      </div>`,
      {
        title: '掃討チケットがなくなりました',
        className: 'sheet-wrap navy-sheet kd-wrap',
        onClose: () => {
          if (m.body.querySelector<HTMLInputElement>('[data-hide]')?.checked) {
            try {
              localStorage.setItem(OUT_HINT_KEY, '1');
            } catch {
              /* 残せなければ次も出る */
            }
          }
          done();
        },
      },
    );
    m.body.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('[data-shop]')) return;
      m.close();
      go('shop', { cat: 'perm', focus: 'komedoiya' });
    });
  });
}
